import React, { useState, useEffect } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { apiRequest } from '../../config/api';

/**
 * Reusable resource selector with empty state handling.
 *
 * Props:
 *  - label:          Field label (e.g. "Form", "Source Workflow")
 *  - value:          Currently selected resource ID
 *  - onChange:        Called with the new resource ID
 *  - endpoint:       API endpoint to fetch resources (e.g. "/forms", "/workflows")
 *  - mapItem:        (item) => { value, label } — maps API response items to select options
 *  - emptyIcon:      Lucide icon component for the empty state
 *  - emptyTitle:     Title shown when no resources exist (e.g. "No forms yet")
 *  - emptyMessage:   Help text shown below the title
 *  - emptyAction:    Button label (e.g. "Save Work & Create Form")
 *  - onEmptyAction:  Called when the empty-state button is clicked
 *  - placeholder:    Placeholder for the dropdown (default: "Select…")
 *  - filterItems:    Optional (items) => filteredItems to exclude certain resources
 *  - children:       Optional content rendered below the dropdown when a value is selected
 */
export default function ResourceSelector({
  label,
  value,
  onChange,
  endpoint,
  mapItem,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyMessage,
  emptyAction,
  onEmptyAction,
  placeholder = 'Select…',
  filterItems,
  children,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest(endpoint)
      .then((r) => r.json())
      .then((data) => {
        const mapped = (Array.isArray(data) ? data : []).map(mapItem);
        setItems(filterItems ? filterItems(mapped, data) : mapped);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        <div className="flex flex-col items-center py-4 px-3 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
          {EmptyIcon && (
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mb-2">
              <EmptyIcon className="w-4 h-4 text-gray-400" />
            </div>
          )}
          <p className="text-xs font-medium text-gray-700 mb-0.5">{emptyTitle}</p>
          <p className="text-[11px] text-gray-400 text-center mb-2.5">{emptyMessage}</p>
          {onEmptyAction && emptyAction && (
            <button
              onClick={onEmptyAction}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
            >
              {emptyAction}
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors bg-white"
      >
        <option value="">{placeholder}</option>
        {items.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {value && children}
    </div>
  );
}
